// Persistent FIFO queue of HTTP mutations stored in IndexedDB (see localdb.ts).
//
// Replay rules:
//  * strictly in insertion order, one request at a time (one tab at a time, via a Web Lock);
//  * network error / 5xx / 408 / 429 / a 2xx that did not come from our API (followed redirect,
//    non-JSON body, a created clip answered with something else) -> stop, retry later with
//    exponential backoff (2s .. 60s), and immediately on the window "online" event or flush();
//  * 401 -> stop and emit an auth-required status (the op stays queued); a login in any tab resumes;
//  * 403 `restricted` (the session may only change the password / set up 2FA) -> stop, the op stays
//    queued; the session gate (see setSessionGate) is asked again before the next attempt;
//  * with a session gate installed (the app does, see session.ts), nothing is sent until it allows
//    it: queued work of one user is never sent with another user's session. Every op carries the
//    id of the user who queued it (`userId`; ops of older versions have none);
//  * a logout this device could not tell the server yet (offline) is sent first, before any op
//    (queueLogout / sendQueuedLogout);
//  * other 4xx -> the op is dropped and listed in `status.failed` together with its content (also
//    kept in the cache store across reloads). A refused upload is never thrown away: its audio is
//    first stored as a recoverable recording; when even that fails, the op stays queued but parked
//    (`rejected`, never replayed) and is offered for recovery and download (see parkedUploads);
//  * a successful op emits a change event with its docId so open views refetch, and is remembered
//    for a while so views built from a stale offline copy still show it (see acceptedOpsFor).
//
// Queue maintenance on enqueue (keeps offline sessions small and fast to replay):
//  * updateDocument / updateClip / updateSegment / saveSettings for the same target are merged into
//    the latest queued (not in-flight) op of the same kind instead of being appended;
//  * deleteDocument / deleteClip drop the queued ops of that document / clip first and are then
//    always queued themselves: whether an earlier creation reached the server cannot be known
//    reliably, and the server answers 204/404 for ids it does not have (both count as done).
//
// Storage: ops live in IndexedDB. When a write fails (quota, closed connection) or IndexedDB is not
// usable, the op is kept in memory instead and enqueue() reports `persisted: false` (with a
// storage warning). Memory ops are replayed after the stored ones; later ops of the same document
// also stay in memory until those are sent, so nothing overtakes them. A memory upload protects
// the recorder's crash-recovery copy of its audio (`recoveryId`), which is only deleted once the
// server accepted the upload. When IndexedDB answers late (slow open), memory ops are moved there.
import type { IDBPTransaction } from 'idb';
import { ApiClientError, authEpoch, bumpAuthEpoch, request, upload } from './http';
import {
  cacheDelete,
  cacheEntries,
  cacheGetStrict,
  cachePut,
  clearNotPersistedWarning,
  deleteRecordingData,
  getDb,
  getRecordingChunks,
  getRecordingRecord,
  holdRecording,
  invalidateDb,
  isStorageSlow,
  reportStorageError,
  saveRecordingChunk,
  StorageNotReadyError,
  subscribeStorageReady,
  warnNotPersisted,
  type BaamDB,
  type LocalDb,
  type RecordingRecord,
  type StoredOutboxOp,
} from './localdb';

export type OutboxOpKind =
  | 'createDocument'
  | 'updateDocument'
  | 'deleteDocument'
  | 'createAudioClip'
  | 'createTextClip'
  | 'updateClip'
  | 'deleteClip'
  | 'retranscribeClip'
  | 'updateSegment'
  | 'saveSettings';

export interface OutboxOp {
  /** Auto-increment key (insertion order). Memory ops use keys from MEMORY_SEQ_BASE up. */
  seq?: number;
  kind: OutboxOpKind;
  docId: string | null;
  clipId?: string;
  segmentId?: string;
  method: 'PUT' | 'PATCH' | 'DELETE' | 'POST';
  url: string;
  /** JSON body (for createAudioClip: the text fields sent alongside the file). */
  body?: unknown;
  /** createAudioClip only: the recorded audio. */
  blob?: Blob;
  blobMimeType?: string;
  /**
   * createAudioClip only: id of the crash-recovery copy of the same audio (recorder.ts). It is
   * deleted once the server accepted the upload, and protected while the op is only in memory.
   */
  recoveryId?: string;
  /**
   * createDocument only: the id was generated on this device for a new document, so the server
   * cannot know it before this op is sent. Not set when the document may exist already (a
   * recovered recording's document is created again, which is idempotent).
   */
  newDocument?: boolean;
  createdAt: string;
  attempts: number;
  lastError?: string;
  /**
   * The server refused this upload and its audio could not be stored as a recoverable recording:
   * the op is kept, never replayed, and offered for recovery instead (see parkedUploads).
   */
  rejected?: string;
  /**
   * The signed-in user who queued the op: it is only sent with that user's session (see the session
   * gate). Missing on ops queued by an older version and without accounts.
   */
  userId?: string;
}

export interface EnqueueResult {
  /** False when the op could only be kept in memory: it is lost if the page closes before it is sent. */
  persisted: boolean;
}

export interface FailedOp {
  kind: OutboxOpKind;
  docId: string | null;
  message: string;
  at: string;
  /** The refused content (typed text, title, ...), so it can still be copied; absent when there was none. */
  detail?: string;
}

export interface OutboxStatus {
  online: boolean;
  /** Number of queued ops. */
  pending: number;
  /** Number of queued audio uploads. */
  pendingUploads: number;
  /** Total bytes of queued audio. */
  pendingBytes: number;
  syncing: boolean;
  authRequired: boolean;
  /** Last replay error message (Slovenian), null when the last attempt succeeded. */
  lastError: string | null;
  /** Ops dropped because the server rejected them (4xx), most recent first, max 20; kept across reloads. */
  failed: FailedOp[];
  /** True while the browser storage does not answer: ops queued earlier may be missing from the counts. */
  pendingUnknown: boolean;
}

const BASE_DELAY_MS = 2_000;
const MAX_DELAY_MS = 60_000;
const MAX_FAILED = 20;
const UPLOAD_BASE_TIMEOUT_MS = 60_000;
const CHANNEL_NAME = 'baam-outbox';
const REPLAY_LOCK = 'baam-outbox-replay';
const AUTH_MESSAGE = 'Prijava je potekla. Prijavite se znova, da se shranjene spremembe pošljejo na strežnik.';
const RESTRICTED_MESSAGE = 'Spremembe bodo poslane, ko nastavite novo geslo oziroma vklopite dvostopenjsko prijavo.';
const MSG_OFFLINE = 'Ni povezave s strežnikom.';
const MSG_UNEXPECTED = 'Strežnik je vrnil nepričakovan odgovor.';
const MSG_UNREADABLE = 'Posnetka ni bilo mogoče prebrati iz shrambe brskalnika.';
/** Cache keys of refused ops: `failed:<ISO time>:<seq>`. */
const FAILED_KEY_PREFIX = 'failed:';
/** Accepted ops are laid over stale offline copies for at most this long. */
const ACCEPTED_TTL_MS = 10 * 60_000;
/** Memory op keys start far above IndexedDB's auto-increment keys, at a random offset per tab. */
const MEMORY_SEQ_BASE = 1_000_000_000_000;
/** Recovery ids of parked uploads: `outbox:<seq>`. */
const PARKED_PREFIX = 'outbox:';

// ---------- queue storage ----------

/** Changes computed against a snapshot of queued ops and applied together. */
interface QueueChanges {
  add?: StoredOutboxOp[];
  put?: StoredOutboxOp[];
  remove?: number[];
}

/** Which queued ops `planEnqueue` must see: none, one document's, or all. */
type Scope = { kind: 'none' } | { kind: 'doc'; docId: string } | { kind: 'all' };

type OutboxTx = IDBPTransaction<BaamDB, ['outbox'], 'readwrite'>;

function withoutSeq(op: StoredOutboxOp): StoredOutboxOp {
  const copy = { ...op };
  delete copy.seq;
  return copy;
}

function errorName(err: unknown): string {
  return typeof err === 'object' && err !== null && 'name' in err ? String((err as { name: unknown }).name) : '';
}

/** The browser closed the connection under us: worth one retry on a fresh connection. */
const isClosedConnection = (err: unknown) => errorName(err) === 'InvalidStateError';

/** A request whose outcome is read from its transaction's `done`: keep its rejection quiet. */
function quiet(promise: Promise<unknown>): void {
  promise.catch(() => {});
}

function abortQuietly(tx: OutboxTx): void {
  quiet(tx.done);
  try {
    tx.abort();
  } catch {
    // Already finished or aborted.
  }
}

const bySeq = (a: StoredOutboxOp, b: StoredOutboxOp) => (a.seq ?? 0) - (b.seq ?? 0);
const isMemorySeq = (seq: number | null | undefined): seq is number => typeof seq === 'number' && seq >= MEMORY_SEQ_BASE;

/** Ops kept in memory, in insertion order (see the header). */
let memoryOps: StoredOutboxOp[] = [];
let nextMemorySeq = MEMORY_SEQ_BASE + Math.floor(Math.random() * 2 ** 30) * 4096;
/** Releases of the recording holds of memory uploads, by seq (see localdb.holdRecording). */
const memoryHolds = new Map<number, () => void>();
/** Sent, but could not be removed from IndexedDB: not replayed again in this session. */
const sessionDone = new Set<number>();
/** Refused, audio not rescued and the parking could not be stored: parked for this session (seq -> reason). */
const sessionParked = new Map<number, string>();

function parkReason(op: StoredOutboxOp): string | undefined {
  return op.rejected ?? (op.seq !== undefined ? sessionParked.get(op.seq) : undefined);
}

/** An op that is still to be sent (not parked, not already done): part of replay, views and counts. */
function isLive(op: StoredOutboxOp): boolean {
  return parkReason(op) === undefined && !(op.seq !== undefined && sessionDone.has(op.seq));
}

/** Drop the "kept in memory only" warning once nothing is (a parked upload in memory counts). */
function clearWarningIfStored(): void {
  if (memoryOps.length === 0) clearNotPersistedWarning();
}

function addMemoryOp(op: StoredOutboxOp): void {
  const stored: StoredOutboxOp = { ...withoutSeq(op), seq: nextMemorySeq++ };
  memoryOps.push(stored);
  // The crash copy of this audio is the only durable one now: keep it (and keep it from being
  // offered for recovery) until the server has the upload.
  if (stored.kind === 'createAudioClip' && stored.recoveryId) memoryHolds.set(stored.seq!, holdRecording(stored.recoveryId));
}

function putMemoryOp(op: StoredOutboxOp): void {
  const i = memoryOps.findIndex((o) => o.seq === op.seq);
  if (i >= 0) memoryOps[i] = { ...op };
}

function releaseMemoryHold(seq: number): void {
  memoryHolds.get(seq)?.();
  memoryHolds.delete(seq);
}

/** `pruned`: the user deleted the op's clip or document, so its recovery copy goes as well. */
function removeMemoryOp(seq: number, pruned = false): void {
  const op = memoryOps.find((o) => o.seq === seq);
  if (!op) return;
  memoryOps = memoryOps.filter((o) => o.seq !== seq);
  releaseMemoryHold(seq);
  if (pruned && op.recoveryId) void deleteRecovery(op.recoveryId);
}

function memoryInScope(scope: Scope): StoredOutboxOp[] {
  if (scope.kind === 'none') return [];
  return memoryOps
    .filter((o) => isLive(o) && (scope.kind === 'all' || o.docId === scope.docId))
    .map((o) => ({ ...o }));
}

/**
 * Run `fn` on the IndexedDB connection, replacing a connection the browser closed once.
 * Resolves undefined when IndexedDB is not usable (`required`: rejects instead).
 */
async function withDb<T>(fn: (db: LocalDb) => Promise<T>, required = false): Promise<T | undefined> {
  for (let attempt = 0; ; attempt++) {
    const db = await getDb();
    if (!db) {
      if (required) throw new Error('IndexedDB is not available');
      return undefined;
    }
    try {
      return await fn(db);
    } catch (err) {
      if (attempt === 0 && isClosedConnection(err)) {
        invalidateDb(db);
        continue;
      }
      throw err;
    }
  }
}

/**
 * Every op (stored ones first, in order, then memory ops), parked ones included. With `strict`,
 * a failing read – or storage that has not answered yet – rejects instead of leaving the stored
 * ops out.
 */
async function everyOp(strict = false): Promise<StoredOutboxOp[]> {
  let stored: StoredOutboxOp[] = [];
  try {
    const all = await withDb((db) => db.getAll('outbox'));
    if (all === undefined && strict && isStorageSlow()) throw new StorageNotReadyError();
    stored = all ?? [];
  } catch (err) {
    if (strict) throw err;
    reportStorageError(err);
  }
  return [...stored.sort(bySeq), ...memoryOps.map((o) => ({ ...o }))];
}

async function docOps(docId: string): Promise<StoredOutboxOp[]> {
  let stored: StoredOutboxOp[] = [];
  try {
    stored = (await withDb((db) => db.getAllFromIndex('outbox', 'docId', docId))) ?? [];
  } catch (err) {
    reportStorageError(err);
  }
  return [...stored.sort(bySeq), ...memoryOps.filter((o) => o.docId === docId).map((o) => ({ ...o }))];
}

async function firstStored(db: LocalDb): Promise<StoredOutboxOp | undefined> {
  let cursor = await db.transaction('outbox').store.openCursor();
  while (cursor) {
    if (isLive(cursor.value)) return cursor.value;
    cursor = await cursor.continue();
  }
  return undefined;
}

/**
 * The next op to send: stored ops first (a memory op is never older than a stored op of the same
 * document), then memory ops. Rejects when IndexedDB cannot be read, so nothing overtakes its ops.
 */
async function nextOp(): Promise<StoredOutboxOp | undefined> {
  const stored = await withDb(firstStored);
  if (stored) return stored;
  const op = memoryOps.find(isLive);
  return op ? { ...op } : undefined;
}

/**
 * Atomically replace or delete one op; `fn` returns null to delete, undefined to keep.
 * Resolves true when the op was deleted. Rejects when a stored op cannot be written.
 */
async function updateOne(seq: number, fn: (op: StoredOutboxOp) => StoredOutboxOp | null | undefined): Promise<boolean> {
  if (isMemorySeq(seq)) {
    const i = memoryOps.findIndex((o) => o.seq === seq);
    const current = memoryOps[i];
    if (!current) return false;
    const next = fn({ ...current });
    if (next === null) {
      removeMemoryOp(seq);
      return true;
    }
    if (next !== undefined) memoryOps[i] = { ...next, seq };
    return false;
  }
  const deleted = await withDb(async (db) => {
    const tx = db.transaction('outbox', 'readwrite');
    let removed = false;
    try {
      const current = await tx.store.get(seq);
      if (current) {
        const next = fn(current);
        if (next === null) {
          quiet(tx.store.delete(seq));
          removed = true;
        } else if (next !== undefined) {
          quiet(tx.store.put(next));
        }
      }
    } catch (err) {
      abortQuietly(tx);
      throw err;
    }
    await tx.done;
    return removed;
  }, true);
  return deleted === true;
}

let planChain: Promise<unknown> = Promise.resolve();

/** Queue changes (enqueue, moving memory ops to IndexedDB) run one after the other. */
function serialized<T>(fn: () => Promise<T>): Promise<T> {
  const run = planChain.then(fn, fn);
  planChain = run.catch(() => {});
  return run;
}

/** Applies changes computed against memory ops only. Resolves true when nothing new had to be kept. */
function applyToMemory(changes: QueueChanges): boolean {
  let kept = false;
  for (const seq of changes.remove ?? []) removeMemoryOp(seq, true);
  for (const op of changes.put ?? []) {
    putMemoryOp(op);
    kept = true;
  }
  for (const op of changes.add ?? []) {
    addMemoryOp(op);
    kept = true;
  }
  return !kept;
}

/**
 * Read the queued ops in `scope`, let `plan` decide, and apply the result: in one IndexedDB
 * transaction where possible, in memory otherwise. Resolves true when everything new is stored.
 */
function applyPlan(scope: Scope, docId: string | null, plan: (ops: StoredOutboxOp[]) => QueueChanges): Promise<boolean> {
  return serialized(async () => {
    for (let attempt = 0; ; attempt++) {
      const db = await getDb();
      // localdb already warns that IndexedDB cannot be used.
      if (!db) return applyToMemory(plan(memoryInScope(scope)));
      let tx: OutboxTx;
      let stored: StoredOutboxOp[];
      try {
        tx = db.transaction('outbox', 'readwrite');
      } catch (err) {
        if (attempt === 0 && isClosedConnection(err)) {
          invalidateDb(db);
          continue;
        }
        warnNotPersisted(err);
        return applyToMemory(plan(memoryInScope(scope)));
      }
      try {
        stored =
          scope.kind === 'none'
            ? []
            : scope.kind === 'doc'
              ? await tx.store.index('docId').getAll(scope.docId)
              : await tx.store.getAll();
      } catch (err) {
        abortQuietly(tx);
        if (attempt === 0 && isClosedConnection(err)) {
          invalidateDb(db);
          continue;
        }
        warnNotPersisted(err);
        return applyToMemory(plan(memoryInScope(scope)));
      }

      // Synchronous from here until the writes are issued: memory ops cannot change meanwhile, and
      // the transaction stays active (no non-IDB awaits between its read and its writes).
      const live = stored.filter(isLive).sort(bySeq);
      const changes = plan([...live, ...memoryInScope(scope)]);
      const storedBySeq = new Map(live.map((o) => [o.seq!, o]));
      const removeStored: StoredOutboxOp[] = [];
      const putStored: StoredOutboxOp[] = [];
      const addStored: StoredOutboxOp[] = [];
      let kept = false;
      for (const seq of changes.remove ?? []) {
        if (isMemorySeq(seq)) removeMemoryOp(seq, true);
        else {
          const op = storedBySeq.get(seq);
          if (op) removeStored.push(op);
        }
      }
      for (const op of changes.put ?? []) {
        if (isMemorySeq(op.seq)) {
          putMemoryOp(op);
          kept = true;
        } else {
          putStored.push(op);
        }
      }
      // FIFO: an op must not be stored while an earlier op of its document waits in memory.
      const behindMemory = memoryOps.some((o) => isLive(o) && o.docId === docId);
      for (const op of changes.add ?? []) {
        if (behindMemory) {
          addMemoryOp(op);
          kept = true;
        } else {
          addStored.push(op);
        }
      }
      if (removeStored.length + putStored.length + addStored.length === 0) {
        quiet(tx.done);
        return !kept;
      }
      try {
        for (const op of removeStored) quiet(tx.store.delete(op.seq!));
        for (const op of putStored) quiet(tx.store.put(op));
        for (const op of addStored) quiet(tx.store.add(withoutSeq(op)));
        await tx.done;
      } catch (err) {
        abortQuietly(tx);
        // A failed transaction stores nothing: keep the new content in memory instead. A merged
        // op is appended whole after its stored original, which gives the same end result; ops
        // that were to be removed stay queued, and the DELETE that follows them still wins.
        for (const op of [...putStored, ...addStored]) addMemoryOp(op);
        warnNotPersisted(err);
        return false;
      }
      for (const op of removeStored) if (op.recoveryId) void deleteRecovery(op.recoveryId);
      return !kept;
    }
  });
}

let drainRequested = false;

/** IndexedDB answered after all (slow open): store the memory ops there, in order. */
function drainMemory(): Promise<void> {
  return serialized(async () => {
    drainRequested = false;
    if (memoryOps.length === 0) return;
    if (isMemorySeq(inFlightSeq)) {
      // Keep the order: move them once the op in flight is done.
      drainRequested = true;
      return;
    }
    const moving = [...memoryOps];
    try {
      const stored = await withDb(async (db) => {
        const tx = db.transaction('outbox', 'readwrite');
        try {
          for (const op of moving) quiet(tx.store.add(withoutSeq(op)));
        } catch (err) {
          abortQuietly(tx);
          throw err;
        }
        await tx.done;
        return true;
      });
      if (!stored) return;
    } catch (err) {
      reportStorageError(err);
      return;
    }
    const moved = new Set(moving.map((o) => o.seq));
    memoryOps = memoryOps.filter((o) => !moved.has(o.seq));
    for (const op of moving) {
      releaseMemoryHold(op.seq!);
      sessionAttempts.delete(op.seq!);
    }
    clearWarningIfStored();
  });
}

// ---------- enqueue planning (coalescing / pruning) ----------

const MERGEABLE: ReadonlySet<OutboxOpKind> = new Set(['updateDocument', 'updateClip', 'updateSegment', 'saveSettings']);
const CLIP_CREATE: ReadonlySet<OutboxOpKind> = new Set(['createAudioClip', 'createTextClip']);

function mergeTarget(op: OutboxOp): string | null {
  switch (op.kind) {
    case 'updateDocument':
      return op.docId;
    case 'updateClip':
      return op.clipId ?? null;
    case 'updateSegment':
      return op.segmentId ?? null;
    case 'saveSettings':
      return 'settings';
    default:
      return null;
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function mergeBodies(kind: OutboxOpKind, earlier: unknown, later: unknown): unknown {
  if (kind === 'saveSettings' || !isRecord(earlier) || !isRecord(later)) return later;
  const merged: Record<string, unknown> = { ...earlier, ...later };
  if (kind === 'updateDocument' && isRecord(earlier.meta) && isRecord(later.meta)) {
    merged.meta = { ...earlier.meta, ...later.meta };
  }
  return merged;
}

/**
 * Would merging `op` into an earlier op change the outcome? True when an op queued after the
 * merge candidate touches the same clip (e.g. a clip text replace between two segment edits).
 */
function hasLaterConflict(later: StoredOutboxOp[], op: OutboxOp): boolean {
  if (op.kind === 'updateClip') return later.some((o) => o.clipId === op.clipId);
  if (op.kind === 'updateSegment') return later.some((o) => o.clipId === op.clipId && o.kind !== 'updateSegment');
  return false;
}

/** Decide how a new op changes the queue. `ops` are the queued ops in `scopeFor(op)`, in order. */
export function planEnqueue(
  ops: StoredOutboxOp[],
  op: StoredOutboxOp,
  isBusy: (seq: number | undefined) => boolean,
): QueueChanges {
  if (op.kind === 'deleteDocument' && op.docId) {
    const removable = ops.filter((o) => o.docId === op.docId && !isBusy(o.seq));
    return { remove: removable.map((o) => o.seq!), add: [op] };
  }

  if (op.kind === 'deleteClip' && op.clipId) {
    const removable = ops.filter((o) => o.clipId === op.clipId && !isBusy(o.seq));
    return { remove: removable.map((o) => o.seq!), add: [op] };
  }

  if (MERGEABLE.has(op.kind)) {
    const target = mergeTarget(op);
    for (let i = ops.length - 1; i >= 0; i--) {
      const candidate = ops[i]!;
      if (candidate.kind !== op.kind || mergeTarget(candidate) !== target) continue;
      // Another user's change is never sent under this one's name (nor the other way round).
      if (candidate.userId !== op.userId || isBusy(candidate.seq) || hasLaterConflict(ops.slice(i + 1), op)) break;
      return {
        put: [{ ...candidate, body: mergeBodies(op.kind, candidate.body, op.body), rev: (candidate.rev ?? 0) + 1 }],
      };
    }
  }

  return { add: [op] };
}

function scopeFor(op: OutboxOp): Scope {
  if (op.kind === 'saveSettings') return { kind: 'all' };
  if (op.kind === 'createDocument' || CLIP_CREATE.has(op.kind) || op.kind === 'retranscribeClip') return { kind: 'none' };
  return op.docId ? { kind: 'doc', docId: op.docId } : { kind: 'all' };
}

// ---------- status & events ----------

let status: OutboxStatus = {
  online: typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  pending: 0,
  pendingUploads: 0,
  pendingBytes: 0,
  syncing: false,
  authRequired: false,
  lastError: null,
  failed: [],
  pendingUnknown: false,
};
const statusListeners = new Set<(status: OutboxStatus) => void>();
const docListeners = new Map<string, Set<() => void>>();

function setStatus(patch: Partial<OutboxStatus>): void {
  const next = { ...status, ...patch };
  const changed = (Object.keys(patch) as Array<keyof OutboxStatus>).some((k) => next[k] !== status[k]);
  if (!changed) return;
  status = next;
  for (const listener of [...statusListeners]) {
    try {
      listener(status);
    } catch (err) {
      console.error('[outbox] status listener failed', err);
    }
  }
}

let statusRefreshSeq = 0;

async function refreshStatus(): Promise<void> {
  const mySeq = ++statusRefreshSeq;
  try {
    const ops = (await everyOp()).filter(isLive);
    if (mySeq !== statusRefreshSeq) return; // a newer refresh is running
    let uploads = 0;
    let bytes = 0;
    for (const op of ops) {
      if (op.kind !== 'createAudioClip') continue;
      uploads++;
      bytes += op.blob?.size ?? 0;
    }
    setStatus({ pending: ops.length, pendingUploads: uploads, pendingBytes: bytes, pendingUnknown: isStorageSlow() });
  } catch (err) {
    reportStorageError(err);
  }
}

function emitLocal(docId: string | null): void {
  const targets = [...(docId ? (docListeners.get(docId) ?? []) : []), ...(docListeners.get('*') ?? [])];
  for (const listener of targets) {
    try {
      listener();
    } catch (err) {
      console.error('[outbox] document listener failed', err);
    }
  }
}

// Other tabs of the app share the IndexedDB queue; tell them so their views and counters refresh.
let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent<unknown>) => {
      const data = event.data;
      if (!isRecord(data)) return;
      if (data.type === 'auth-cleared') {
        // Signed in again in another tab: the session cookie is shared, so resume here too – once
        // the gate has checked who is signed in now (it may be someone else). A 401 to a request
        // this tab sent before, with the old cookie, says nothing about the new session.
        bumpAuthEpoch();
        logoutQueued = undefined;
        sessionGate?.invalidate();
        emitSessionChanged();
        if (status.authRequired) resumeAfterLogin();
        return;
      }
      if (data.type === 'session-changed') {
        // Signed in, out or as someone else in another tab: the shared cookie changed. Answers to
        // earlier requests are about the old one; the features read that follows decides.
        bumpAuthEpoch();
        logoutQueued = undefined;
        sessionGate?.invalidate();
        emitSessionChanged();
        return;
      }
      if (data.type !== 'changed') return;
      emitLocal(typeof data.docId === 'string' ? data.docId : null);
      void refreshStatus();
    };
  } catch {
    channel = null;
  }
  return channel;
}

function broadcast(message: Record<string, unknown>): void {
  try {
    getChannel()?.postMessage(message);
  } catch {
    // Broadcasting is a convenience; a closed channel must never break the queue.
  }
}

function emitDocument(docId: string | null): void {
  emitLocal(docId);
  broadcast({ type: 'changed', docId });
}

/** Tell open views that a document changed outside the outbox (e.g. minutes were generated). */
export function notifyDocumentChanged(docId: string): void {
  emitDocument(docId);
}

// ---------- refused ops (kept with their content) ----------

interface StoredFailure {
  entry: FailedOp;
  /** The refused op without its audio, so its content is not lost. */
  op: Omit<StoredOutboxOp, 'blob'>;
}

function textOf(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/** Human-readable content of an op: what the user would lose if it was dropped silently. */
function rejectedDetail(op: OutboxOp): string | undefined {
  const body = isRecord(op.body) ? op.body : {};
  const parts: string[] = [];
  const add = (v: unknown) => {
    const t = textOf(v);
    if (t) parts.push(t);
  };
  switch (op.kind) {
    case 'createTextClip':
    case 'updateClip':
    case 'updateSegment':
      add(body.text);
      break;
    case 'createDocument':
    case 'updateDocument':
      add(body.title);
      if (isRecord(body.meta)) Object.values(body.meta).forEach(add);
      if (Array.isArray(body.speakers)) for (const s of body.speakers) if (isRecord(s)) add(s.name);
      break;
    default:
      break;
  }
  return parts.length > 0 ? parts.join('\n') : undefined;
}

const failureKey = (f: FailedOp) => `${f.at}|${f.kind}|${f.docId ?? ''}|${f.message}`;

function isFailedOp(v: unknown): v is FailedOp {
  return isRecord(v) && typeof v.kind === 'string' && typeof v.message === 'string' && typeof v.at === 'string';
}

function mergeFailed(entries: FailedOp[]): FailedOp[] {
  const seen = new Set<string>();
  const out: FailedOp[] = [];
  for (const f of entries) {
    const key = failureKey(f);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(f);
  }
  return out.sort((a, b) => b.at.localeCompare(a.at)).slice(0, MAX_FAILED);
}

async function persistFailure(entry: FailedOp, op: StoredOutboxOp): Promise<void> {
  const { blob: _blob, ...rest } = op;
  const record: StoredFailure = { entry, op: rest };
  await cachePut(`${FAILED_KEY_PREFIX}${entry.at}:${op.seq ?? 0}`, record);
  const all = await cacheEntries<unknown>(FAILED_KEY_PREFIX);
  if (all.length <= MAX_FAILED) return;
  // Keys start with the ISO time: newest first.
  all.sort((a, b) => b.key.localeCompare(a.key));
  for (const old of all.slice(MAX_FAILED)) await cacheDelete(old.key);
}

/** Seqs of ops this tab saw refused, oldest first (bounded): views stop showing them at once. */
const refusedHere = new Set<number>();
const MAX_REFUSED_SEQS = 200;

/**
 * True when this tab saw the server refuse the op with this seq. (An op that left the queue
 * otherwise was sent – here or by another tab – or pruned by a deletion queued after it.)
 */
export function wasRefusedHere(seq: number): boolean {
  return refusedHere.has(seq);
}

function recordRejection(op: StoredOutboxOp, message: string): void {
  if (op.seq !== undefined) {
    refusedHere.add(op.seq);
    if (refusedHere.size > MAX_REFUSED_SEQS) refusedHere.delete(refusedHere.values().next().value!);
  }
  const entry: FailedOp = { kind: op.kind, docId: op.docId, message, at: new Date().toISOString() };
  const detail = rejectedDetail(op);
  if (detail) entry.detail = detail;
  setStatus({ failed: mergeFailed([entry, ...status.failed]) });
  void persistFailure(entry, op).catch((err: unknown) => reportStorageError(err));
}

let failuresLoaded = false;

async function loadFailures(): Promise<void> {
  if (failuresLoaded) return;
  failuresLoaded = true;
  const entries = await cacheEntries<unknown>(FAILED_KEY_PREFIX);
  const stored = entries.map((e) => (isRecord(e.value) ? e.value.entry : undefined)).filter(isFailedOp);
  if (stored.length > 0) setStatus({ failed: mergeFailed([...status.failed, ...stored]) });
}

/** Forget the list of refused changes (also on this device's storage). */
export async function clearFailed(): Promise<void> {
  setStatus({ failed: [] });
  for (const entry of await cacheEntries<unknown>(FAILED_KEY_PREFIX)) await cacheDelete(entry.key);
}

// ---------- accepted ops (shown on top of stale offline copies) ----------

interface AcceptedEntry {
  /** With its seq, so a view can tell it from the queued op it was built with. */
  op: OutboxOp;
  /** Position in the stream of accepted ops (see acceptedMark). */
  n: number;
  at: number;
  /** Still needed by document views / by list views. */
  doc: boolean;
  list: boolean;
}

const LIST_KINDS: ReadonlySet<OutboxOpKind> = new Set(['createDocument', 'updateDocument', 'deleteDocument']);
let accepted: AcceptedEntry[] = [];
let acceptCounter = 0;

function rememberAccepted(op: StoredOutboxOp): void {
  if (!op.docId) return;
  // Never keep audio around for this.
  const { blob: _blob, ...rest } = op;
  accepted.push({ op: rest, n: ++acceptCounter, at: Date.now(), doc: true, list: LIST_KINDS.has(op.kind) });
}

function liveAccepted(): AcceptedEntry[] {
  const cutoff = Date.now() - ACCEPTED_TTL_MS;
  accepted = accepted.filter((e) => e.at >= cutoff && (e.doc || e.list));
  return accepted;
}

/** Forget the accepted ops (their content stays only for the signed-in user, see logout). */
export function clearAccepted(): void {
  accepted = [];
}

/** Current position in the stream of accepted ops: take it before a server read starts. */
export function acceptedMark(): number {
  return acceptCounter;
}

/**
 * Ops of `docId` the server accepted recently, in order. A view built from a stale offline copy
 * applies them, so a change the server already has does not seem to vanish.
 */
export function acceptedOpsFor(docId: string): OutboxOp[] {
  return liveAccepted()
    .filter((e) => e.doc && e.op.docId === docId)
    .map((e) => ({ ...e.op }));
}

/** Document-level ops (create / update / delete) accepted recently, for stale offline lists. */
export function acceptedListOps(): OutboxOp[] {
  return liveAccepted()
    .filter((e) => e.list)
    .map((e) => ({ ...e.op }));
}

/**
 * A server read that started at `mark` succeeded: it contains every op accepted up to then.
 * With `docId` it was that document, without it a document list.
 */
export function forgetAccepted(mark: number, docId?: string): void {
  for (const e of accepted) {
    if (e.n > mark) continue;
    if (docId === undefined) e.list = false;
    else if (e.op.docId === docId) e.doc = false;
  }
  liveAccepted();
}

// ---------- enqueue ----------

let persistRequested = false;

function requestPersistentStorage(): void {
  if (persistRequested) return;
  persistRequested = true;
  try {
    // Ask the browser not to evict our storage under pressure (queued recordings live there).
    void navigator.storage?.persist?.().catch(() => false);
  } catch {
    // Not supported – nothing to do.
  }
}

/** enqueue() calls that have not settled yet (see whenWritesSettled). */
const pendingWrites = new Set<Promise<unknown>>();

/**
 * Append an op and trigger a replay attempt. Resolves once the op is stored – in IndexedDB
 * (`persisted: true`) or, when that failed, in memory only (`persisted: false`).
 */
export function enqueue(input: Omit<OutboxOp, 'seq' | 'attempts' | 'createdAt'>): Promise<EnqueueResult> {
  const write = enqueueOp(input);
  pendingWrites.add(write);
  const settled = () => {
    pendingWrites.delete(write);
  };
  write.then(settled, settled);
  return write;
}

/**
 * Resolves once every enqueue() started so far has settled (stored, or failed). An action that
 * reads the document right after a tap (export, copy) waits for this first, so an edit the same
 * tap committed (a text field saved on blur) is part of what it reads.
 */
export async function whenWritesSettled(): Promise<void> {
  await Promise.allSettled([...pendingWrites]);
}

/** True while an enqueue() has not settled yet. */
export function hasPendingWrites(): boolean {
  return pendingWrites.size > 0;
}

async function enqueueOp(input: Omit<OutboxOp, 'seq' | 'attempts' | 'createdAt'>): Promise<EnqueueResult> {
  const op: StoredOutboxOp = { ...input, createdAt: new Date().toISOString(), attempts: 0 };
  delete op.seq;
  delete op.rejected;
  if (op.kind === 'createAudioClip') requestPersistentStorage();
  const persisted = await applyPlan(scopeFor(op), op.docId, (ops) => planEnqueue(ops, op, isBusy));
  if (persisted) clearWarningIfStored();
  emitDocument(op.docId);
  void refreshStatus();
  flush();
  return { persisted };
}

function withSessionAttempts(op: StoredOutboxOp): OutboxOp {
  const extra = op.seq === undefined ? undefined : sessionAttempts.get(op.seq);
  return extra === undefined ? op : { ...op, attempts: Math.max(op.attempts, extra) };
}

/** All queued ops (in order). */
export async function listOps(): Promise<OutboxOp[]> {
  return (await everyOp()).filter(isLive).map(withSessionAttempts);
}

/**
 * Every op of this device, queued and parked (with `rejected` set): rejects when the stored ones
 * cannot be read (also while storage has not answered yet), so a "nothing here" is never a guess.
 */
export async function listAllOpsStrict(): Promise<OutboxOp[]> {
  const out: OutboxOp[] = [];
  for (const op of await everyOp(true)) {
    if (op.seq !== undefined && sessionDone.has(op.seq)) continue;
    const reason = parkReason(op);
    out.push(reason !== undefined && op.rejected === undefined ? { ...op, rejected: reason } : op);
  }
  return out;
}

/** Queued ops for one document (in order). */
export async function opsForDocument(docId: string): Promise<OutboxOp[]> {
  return (await docOps(docId)).filter(isLive).map(withSessionAttempts);
}

/** Object URL for the blob of a clip that is still waiting to be uploaded, or null. Caller must revoke it. */
export async function localAudioUrl(clipId: string): Promise<string | null> {
  const ops = await everyOp();
  const op = ops.find((o) => o.kind === 'createAudioClip' && o.clipId === clipId && o.blob);
  if (!op?.blob) return null;
  return URL.createObjectURL(audioBlob(op));
}

// ---------- parked uploads and recovery copies (used by recorder.ts) ----------

export interface ParkedUpload {
  /** Recovery id (`outbox:<seq>`), accepted by recorder.recoverRecording / discardRecording. */
  id: string;
  op: OutboxOp;
  reason: string;
}

export function isParkedId(id: string): boolean {
  return id.startsWith(PARKED_PREFIX);
}

function seqOfParkedId(id: string): number | null {
  const seq = Number(id.slice(PARKED_PREFIX.length));
  return Number.isSafeInteger(seq) && seq > 0 ? seq : null;
}

/** Refused uploads whose audio could only be kept in the queue. */
export async function parkedUploads(): Promise<ParkedUpload[]> {
  const out: ParkedUpload[] = [];
  for (const op of await everyOp()) {
    const reason = parkReason(op);
    if (op.kind === 'createAudioClip' && op.blob && reason !== undefined && op.seq !== undefined) {
      out.push({ id: `${PARKED_PREFIX}${op.seq}`, op, reason });
    }
  }
  return out;
}

export async function parkedUpload(id: string): Promise<ParkedUpload | undefined> {
  return (await parkedUploads()).find((p) => p.id === id);
}

/** Recovery ids queued uploads still refer to: those copies are not offered for recovery as well. */
export async function queuedRecoveryIds(): Promise<Set<string>> {
  const ids = new Set<string>();
  for (const op of await everyOp()) {
    if (op.recoveryId && !(op.seq !== undefined && sessionDone.has(op.seq))) ids.add(op.recoveryId);
  }
  return ids;
}

/** Delete what a recovery id stands for: a parked upload, or the recorder's crash-recovery copy. */
export async function discardRecovery(id: string): Promise<void> {
  if (!isParkedId(id)) {
    await deleteRecordingData(id);
    return;
  }
  const seq = seqOfParkedId(id);
  if (seq === null) return;
  await updateOne(seq, (cur) => (parkReason(cur) !== undefined ? null : undefined));
  sessionParked.delete(seq);
  clearWarningIfStored();
  void refreshStatus();
}

function deleteRecovery(id: string): Promise<void> {
  return discardRecovery(id).catch((err: unknown) => reportStorageError(err));
}

// ---------- replay ----------

/** Seq of the op whose request is running (or whose result is being recorded). */
let inFlightSeq: number | null = null;
/** Failed attempts in this session (shown via listOps; not written to storage). */
const sessionAttempts = new Map<number, number>();
let replaying = false;
let rerunRequested = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let nextDelayMs = BASE_DELAY_MS;

function isBusy(seq: number | undefined): boolean {
  return seq !== undefined && seq === inFlightSeq;
}

/** Start replaying now (no-op if already running). */
export function flush(): void {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  if (replaying) {
    rerunRequested = true;
    return;
  }
  void runReplay();
}

function scheduleRetry(): void {
  if (retryTimer !== null) clearTimeout(retryTimer);
  const delay = nextDelayMs;
  nextDelayMs = Math.min(nextDelayMs * 2, MAX_DELAY_MS);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    flush();
  }, delay);
}

async function runReplay(): Promise<void> {
  replaying = true;
  setStatus({ syncing: true });
  try {
    do {
      rerunRequested = false;
      if (drainRequested) await drainMemory();
      await withReplayLock(replayPass);
      // flush() was called during the pass (new op, online event, ...): retry now, not after the backoff.
      if (rerunRequested && retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
    } while (rerunRequested && !status.authRequired);
  } catch (err) {
    // Storage failures while reading the queue: try again later.
    reportStorageError(err);
    scheduleRetry();
  } finally {
    replaying = false;
    setStatus({ syncing: false });
  }
}

/**
 * Only one tab replays at a time (Web Locks, where supported): the stored queue is shared, and a
 * tab holding the lock sends our stored ops too. Memory ops are sent by their own tab.
 */
async function withReplayLock(pass: () => Promise<void>): Promise<void> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (typeof locks?.request !== 'function') return pass();
  await locks.request(REPLAY_LOCK, { ifAvailable: true }, async (lock) => {
    if (!lock) {
      scheduleRetry();
      return;
    }
    await pass();
  });
}

type Outcome =
  | { type: 'ok' }
  | { type: 'retry'; message: string }
  | { type: 'auth' }
  /** 401 to a request sent before a sign-in (here or in another tab): send again with the new session. */
  | { type: 'stale' }
  | { type: 'restricted' }
  | { type: 'rejected'; message: string };

/** What the session gate says about `op`; a failing check counts as "try again later". */
async function askGate(gate: SessionGate, op: OutboxOp): Promise<GateVerdict> {
  try {
    return await gate.check(op);
  } catch {
    return 'retry';
  }
}

async function replayPass(): Promise<void> {
  // A logout the server has not heard of yet comes before everything else.
  if (!(await sendQueuedLogout())) {
    setStatus({ lastError: MSG_OFFLINE });
    scheduleRetry();
    return;
  }
  for (;;) {
    if (status.authRequired) return;
    let op = await nextOp();
    // Read again after asking: the op may have been merged or sent (another tab) meanwhile; a
    // different op at the head is asked about again (it may be another user's).
    for (let asked: number | undefined; op && sessionGate && op.seq !== asked; op = await nextOp()) {
      const verdict = await askGate(sessionGate, op);
      if (verdict === 'hold') return;
      if (verdict === 'retry') {
        setStatus({ lastError: MSG_OFFLINE });
        scheduleRetry();
        return;
      }
      if (status.authRequired) return;
      asked = op.seq;
    }
    if (!op || op.seq === undefined) {
      nextDelayMs = BASE_DELAY_MS;
      setStatus({ lastError: null });
      return;
    }
    const seq = op.seq;
    const sentRev = op.rev ?? 0;
    inFlightSeq = seq;
    try {
      const outcome = await send(op);
      switch (outcome.type) {
        case 'ok':
          await settleAccepted(op, sentRev);
          nextDelayMs = BASE_DELAY_MS;
          setStatus({ lastError: null });
          emitDocument(op.docId);
          void refreshStatus();
          break;
        case 'rejected':
          await settleRejected(op, outcome.message);
          emitDocument(op.docId);
          void refreshStatus();
          break;
        case 'retry':
          sessionAttempts.set(seq, Math.max(op.attempts, sessionAttempts.get(seq) ?? 0) + 1);
          setStatus({ lastError: outcome.message });
          scheduleRetry();
          return;
        case 'stale':
          // The gate checks again who is signed in now before the op goes out again.
          sessionGate?.invalidate();
          break;
        case 'auth':
          sessionGate?.invalidate();
          setStatus({ authRequired: true, lastError: AUTH_MESSAGE });
          emitSessionChanged();
          return;
        case 'restricted':
          if (!sessionGate) {
            // Nobody re-checks the session: keep trying, like an outage.
            setStatus({ lastError: RESTRICTED_MESSAGE });
            scheduleRetry();
            return;
          }
          // The gate holds the queue until the password is changed / 2FA is set up.
          sessionGate.invalidate();
          setStatus({ lastError: RESTRICTED_MESSAGE });
          emitSessionChanged();
          return;
      }
    } finally {
      inFlightSeq = null;
    }
  }
}

async function settleAccepted(op: StoredOutboxOp, sentRev: number): Promise<void> {
  const seq = op.seq!;
  let removed: boolean;
  try {
    // Keep the op when it was merged with newer changes while in flight; the merged body is a
    // superset of what was sent, so re-sending it is idempotent.
    removed = await updateOne(seq, (cur) => ((cur.rev ?? 0) === sentRev ? null : undefined));
  } catch (err) {
    // It is on the server: never send it again in this session (a later one re-sends it harmlessly).
    reportStorageError(err);
    sessionDone.add(seq);
    removed = true;
  }
  sessionAttempts.delete(seq);
  rememberAccepted(op);
  // The server has the audio now: its crash-recovery copy is not needed any more.
  if (removed && op.recoveryId) await deleteRecovery(op.recoveryId);
  clearWarningIfStored();
}

async function settleRejected(op: StoredOutboxOp, message: string): Promise<void> {
  const seq = op.seq!;
  // A refused recording is stored as a recoverable recording before its op goes away.
  if (op.kind === 'createAudioClip' && op.blob && !(await rescueAudio(op, message))) {
    await park(op, message);
  } else {
    try {
      await updateOne(seq, () => null);
    } catch (err) {
      reportStorageError(err);
      sessionDone.add(seq);
    }
  }
  sessionAttempts.delete(seq);
  if (isMemorySeq(seq)) releaseMemoryHold(seq);
  clearWarningIfStored();
  // After the rescue: listeners (the recovery dialog) look for the rescued recording right away.
  recordRejection(op, message);
}

/** Keep a refused upload whose audio could not be rescued: never replayed, offered for recovery. */
async function park(op: StoredOutboxOp, message: string): Promise<void> {
  const seq = op.seq!;
  try {
    await updateOne(seq, (cur) => ({ ...cur, rejected: message }));
  } catch (err) {
    reportStorageError(err);
    sessionParked.set(seq, message);
  }
}

/**
 * The server refused an upload (e.g. 413 too large, 404 document deleted elsewhere). Never throw a
 * recording away: make it a recoverable recording. Resolves false when that could not be stored.
 */
async function rescueAudio(op: StoredOutboxOp, message: string): Promise<boolean> {
  const blob = audioBlob(op);
  const body = isRecord(op.body) ? op.body : {};
  const now = new Date().toISOString();
  const refused = { docId: op.docId, clipId: op.clipId, rejected: true, reason: message, ...(op.userId ? { userId: op.userId } : {}) };

  // The recorder's crash copy of this audio is still there (the upload was kept in memory, or the
  // page closed before discarding it): mark it as refused instead of storing the audio again.
  if (op.recoveryId && !isParkedId(op.recoveryId)) {
    try {
      const existing = await getRecordingRecord(op.recoveryId);
      if (existing) {
        const chunks = await getRecordingChunks(op.recoveryId);
        const complete = chunks.reduce((sum, c) => sum + c.blob.size, 0) === blob.size;
        if (complete && (await saveRecordingChunk({ ...existing, updatedAt: now, meta: { ...existing.meta, ...refused } }))) {
          return true;
        }
      }
    } catch (err) {
      reportStorageError(err);
    }
  }

  const record: RecordingRecord = {
    id: crypto.randomUUID(),
    startedAt: typeof body.createdAt === 'string' ? body.createdAt : op.createdAt,
    updatedAt: now,
    mimeType: blob.type,
    profile: body.diarize === true ? 'meeting' : 'dictation',
    meta: refused,
    durationMs: typeof body.durationMs === 'number' ? body.durationMs : 0,
    bytes: blob.size,
  };
  let saved = false;
  try {
    saved = await saveRecordingChunk(record, { recordingId: record.id, index: 0, blob });
  } catch (err) {
    reportStorageError(err);
  }
  // The new copy is complete: an incomplete crash copy (or the parked original) is not needed.
  if (saved && op.recoveryId) await deleteRecovery(op.recoveryId);
  return saved;
}

// ---------- requests ----------

/** Text fields of a multipart upload, in the order the server expects them before the file. */
const UPLOAD_FIELDS = ['seq', 'createdAt', 'durationMs', 'diarize', 'speakerCount'] as const;

export function extensionForMime(mime: string): string {
  const base = mime.split(';')[0]!.trim().toLowerCase();
  switch (base) {
    case 'audio/mp4':
    case 'audio/x-m4a':
    case 'audio/m4a':
    case 'video/mp4':
      return 'm4a';
    case 'audio/ogg':
    case 'audio/opus':
      return 'ogg';
    case 'audio/wav':
    case 'audio/wave':
    case 'audio/x-wav':
      return 'wav';
    case 'audio/mpeg':
    case 'audio/mp3':
      return 'mp3';
    case 'audio/aac':
      return 'aac';
    case 'audio/flac':
      return 'flac';
    default:
      return 'webm';
  }
}

function audioBlob(op: OutboxOp): Blob {
  const blob = op.blob!;
  const type = op.blobMimeType || blob.type;
  return blob.type === type ? blob : new Blob([blob], { type });
}

export function buildUploadForm(op: OutboxOp): FormData {
  if (!op.blob || !op.clipId) throw new Error('missing audio');
  const body = isRecord(op.body) ? op.body : {};
  const form = new FormData();
  // Text fields first: the server reads them before streaming the file part to disk.
  for (const field of UPLOAD_FIELDS) {
    const value = body[field];
    if (value !== undefined && value !== null) form.append(field, String(value));
  }
  const blob = audioBlob(op);
  // Multipart parsers drop MIME parameters from the file part ("codecs=opus"), so send the full type too.
  if (blob.type) form.append('mimeType', blob.type);
  form.append('audio', blob, `${op.clipId}.${extensionForMime(blob.type)}`);
  return form;
}

/**
 * Total timeout for an upload where progress cannot be observed (no XMLHttpRequest):
 * 60 s plus 1 s per 50 KB. With XMLHttpRequest an upload only times out when it stalls.
 */
export function uploadTimeoutMs(bytes: number): number {
  return UPLOAD_BASE_TIMEOUT_MS + Math.ceil(bytes / 50_000) * 1000;
}

async function send(op: StoredOutboxOp): Promise<Outcome> {
  const sentAt = authEpoch();
  try {
    let result: unknown;
    if (op.kind === 'createAudioClip') {
      let form: FormData;
      try {
        form = buildUploadForm(op);
      } catch {
        return { type: 'rejected', message: MSG_UNREADABLE };
      }
      result = await upload<unknown>(op.url, form, { method: op.method, fallbackTimeoutMs: uploadTimeoutMs(op.blob?.size ?? 0) });
    } else {
      result = await request<unknown>(op.url, { method: op.method, body: op.body });
    }
    // A created clip is answered with that clip; anything else did not come from our server.
    if (CLIP_CREATE.has(op.kind) && !(isRecord(result) && result.id === op.clipId)) {
      return { type: 'retry', message: MSG_UNEXPECTED };
    }
    return { type: 'ok' };
  } catch (err) {
    // Not an ApiClientError: something unexpected while sending; try again later.
    if (!(err instanceof ApiClientError)) return { type: 'retry', message: MSG_OFFLINE };
    if (err.status === 401) return sentAt === authEpoch() ? { type: 'auth' } : { type: 'stale' };
    // Signed in, but only allowed to change the password / set up 2FA: not a refusal of the op.
    if (err.status === 403 && err.code === 'restricted') return { type: 'restricted' };
    if (op.method === 'DELETE' && err.status === 404) return { type: 'ok' };
    if (err.transient) return { type: 'retry', message: err.message };
    return { type: 'rejected', message: err.message };
  }
}

// ---------- lifecycle ----------

let started = false;
let unsubscribeReady: (() => void) | null = null;

const onOnline = () => {
  setStatus({ online: true });
  nextDelayMs = BASE_DELAY_MS;
  flush();
};
const onOffline = () => setStatus({ online: false });
const onVisibility = () => {
  if (document.visibilityState === 'visible') flush();
};

/** A slow IndexedDB answered after all: move memory ops there, show what it holds and replay it. */
const onStorageReady = () => {
  drainRequested = true;
  // Whether a logout is still to be sent could not be read so far: the session is checked again.
  if (logoutQueued === undefined) emitSessionChanged();
  void (async () => {
    await refreshStatus();
    const docIds = new Set((await everyOp()).map((o) => o.docId).filter((id): id is string => id !== null));
    for (const docId of docIds) emitLocal(docId);
    emitLocal(null);
  })();
  flush();
};

/** Start the background replay loop: listens to online/offline/visibilitychange. Idempotent. */
export function startOutbox(): void {
  if (started) return;
  started = true;
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  document.addEventListener('visibilitychange', onVisibility);
  unsubscribeReady = subscribeStorageReady(onStorageReady);
  getChannel();
  setStatus({ online: navigator.onLine !== false });
  void refreshStatus();
  void loadFailures().catch((err: unknown) => reportStorageError(err));
  flush();
}

/** Remove listeners and timers (tests, hot module reload). Queued ops stay stored. */
export function stopOutbox(): void {
  if (started) {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    document.removeEventListener('visibilitychange', onVisibility);
    unsubscribeReady?.();
    unsubscribeReady = null;
    started = false;
  }
  if (retryTimer !== null) clearTimeout(retryTimer);
  retryTimer = null;
  channel?.close();
  channel = null;
}

function resumeAfterLogin(): void {
  nextDelayMs = BASE_DELAY_MS;
  setStatus({ authRequired: false, lastError: null });
  flush();
}

/** Call after a successful login: clears the auth-required state and resumes the replay (in every tab). */
export function clearAuthRequired(): void {
  resumeAfterLogin();
  broadcast({ type: 'auth-cleared' });
}

// ---------- session gate (user accounts) ----------

/** `ok`: send; `hold`: not now (someone else / nobody is signed in); `retry`: unknown yet, try later. */
export type GateVerdict = 'ok' | 'hold' | 'retry';

export interface SessionGate {
  /** Asked before an op is sent: may `op` (the head of the queue) be sent with the current session? */
  check(op: OutboxOp): Promise<GateVerdict>;
  /** The session may have changed (401, `restricted`, a login elsewhere): the next check must find out. */
  invalidate(): void;
}

let sessionGate: SessionGate | null = null;

/**
 * Install (or remove, with null) the gate the replay asks before sending. Without one every op is
 * sent with whatever session the browser has (single-user setups, tests).
 */
export function setSessionGate(gate: SessionGate | null): void {
  sessionGate = gate;
}

const sessionListeners = new Set<() => void>();

function emitSessionChanged(): void {
  for (const listener of [...sessionListeners]) {
    try {
      listener();
    } catch (err) {
      console.error('[outbox] session listener failed', err);
    }
  }
}

/**
 * Notified when the session may have changed: a 401 or `restricted` answer during replay, or a
 * login / logout in another tab of the app. Returns an unsubscribe function.
 */
export function subscribeSessionChanged(listener: () => void): () => void {
  sessionListeners.add(listener);
  return () => {
    sessionListeners.delete(listener);
  };
}

/** Tell the other tabs that this tab signed in, out or as another user (the cookie is shared). */
export function notifySessionChanged(): void {
  broadcast({ type: 'session-changed' });
}

/**
 * A read was answered 401 (the session ended on the server: revoked, expired, user disabled):
 * like a 401 during replay, the login is needed and the queue waits for it.
 */
export function noteUnauthorized(): void {
  if (status.authRequired) return;
  sessionGate?.invalidate();
  setStatus({ authRequired: true, lastError: AUTH_MESSAGE });
  emitSessionChanged();
}

// ---------- logout the server has not heard of yet ----------

const LOGOUT_KEY = 'session:logout';
const LOGOUT_URL = '/api/auth/logout';
/** This tab's knowledge of a queued logout; undefined: read it from storage. */
let logoutQueued: boolean | undefined;

/**
 * Whether a logout of this device still has to reach the server: 'unknown' while storage cannot
 * be read (or has not answered yet). Callers treat 'unknown' as "maybe": the app then shows the
 * login page, but nothing is sent on a guess.
 */
export async function logoutQueuedState(): Promise<'yes' | 'no' | 'unknown'> {
  if (logoutQueued !== undefined) return logoutQueued ? 'yes' : 'no';
  try {
    const queued = (await cacheGetStrict<unknown>(LOGOUT_KEY)) !== undefined;
    logoutQueued ??= queued;
    return logoutQueued ? 'yes' : 'no';
  } catch (err) {
    if (!(err instanceof StorageNotReadyError)) reportStorageError(err);
    return 'unknown';
  }
}

/** Logged out on this device: remember to tell the server (sendQueuedLogout), also across a reload. */
export async function queueLogout(): Promise<void> {
  logoutQueued = true;
  await cachePut(LOGOUT_KEY, { at: new Date().toISOString() });
}

/** Signed in again (a new session): the old logout must not end it. */
export async function dropQueuedLogout(): Promise<void> {
  logoutQueued = false;
  await cacheDelete(LOGOUT_KEY);
}

/**
 * Sends a queued logout (POST /api/auth/logout). Resolves true when none is queued (any more),
 * false when the server could not be told yet. Sending it twice is harmless (the second answer is
 * a 401 or 204 for a session that is gone).
 */
export async function sendQueuedLogout(): Promise<boolean> {
  const epoch = authEpoch();
  if ((await logoutQueuedState()) !== 'yes') return true;
  // A sign-in meanwhile replaced the session: that one must not be ended.
  if (epoch !== authEpoch() || logoutQueued === false) return true;
  try {
    await request<void>(LOGOUT_URL, { method: 'POST' });
  } catch (err) {
    // 401: the session had ended already.
    if (!(err instanceof ApiClientError && err.status === 401)) return false;
  }
  if (epoch === authEpoch()) await dropQueuedLogout();
  return true;
}

/**
 * Delete queued ops of this device, parked uploads included: all of them, or those `which`
 * selects (the previous user's unsent work, when another person signs in and chooses to discard
 * it). Recovery copies the ops refer to are deleted as well. A request in flight is not cancelled;
 * its op is gone afterwards.
 */
export async function discardOps(which: (op: OutboxOp) => boolean = () => true): Promise<void> {
  await serialized(async () => {
    const recoveryIds = new Set<string>();
    const discarded: number[] = [];
    for (const op of memoryOps.filter(which)) {
      if (op.recoveryId && !isParkedId(op.recoveryId)) recoveryIds.add(op.recoveryId);
      discarded.push(op.seq!);
      removeMemoryOp(op.seq!);
    }
    try {
      await withDb(async (db) => {
        const tx = db.transaction('outbox', 'readwrite');
        const all = await tx.store.getAll();
        for (const op of all) {
          if (!which(op)) continue;
          if (op.recoveryId && !isParkedId(op.recoveryId)) recoveryIds.add(op.recoveryId);
          discarded.push(op.seq!);
          quiet(tx.store.delete(op.seq!));
        }
        await tx.done;
      });
    } catch (err) {
      reportStorageError(err);
      throw err;
    }
    for (const seq of discarded) {
      sessionParked.delete(seq);
      sessionAttempts.delete(seq);
    }
    for (const id of recoveryIds) await deleteRecovery(id);
  });
  clearWarningIfStored();
  setStatus({ lastError: null });
  emitLocal(null);
  broadcast({ type: 'changed', docId: null });
  await refreshStatus();
}

export function getStatus(): OutboxStatus {
  return status;
}

/** Subscribe to status changes. Returns an unsubscribe function. */
export function subscribeStatus(listener: (status: OutboxStatus) => void): () => void {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

/**
 * Subscribe to "something changed for this document" events: emitted after an op for docId is
 * enqueued (optimistic update) and after it was accepted by the server. Returns unsubscribe.
 * docId "*" receives events for all documents.
 */
export function subscribeDocument(docId: string, listener: () => void): () => void {
  let set = docListeners.get(docId);
  if (!set) {
    set = new Set();
    docListeners.set(docId, set);
  }
  set.add(listener);
  return () => {
    const current = docListeners.get(docId);
    current?.delete(listener);
    if (current?.size === 0) docListeners.delete(docId);
  };
}
